import type { ReactNode } from "react";
import { LocaleLink as Link } from "../i18n/LocaleLink";
import { ArrowRight } from "lucide-react";
import { BoardMock } from "./Mockups";
import { useLocale } from "../i18n/useLocale";

export function Container({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`mx-auto w-full max-w-content px-4 md:px-10 ${className}`}>{children}</div>;
}

export function Button({
  to,
  children,
  variant = "primary",
  size = "md",
  withArrow = false,
}: {
  to: string;
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "light" | "ghost-light";
  size?: "md" | "lg";
  withArrow?: boolean;
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-xl font-display font-semibold transition-colors duration-150";
  const sizes = size === "lg" ? "px-7 py-3.5 text-[15px]" : "px-5 py-2.5 text-sm";
  const variants: Record<string, string> = {
    primary: "bg-primary text-white hover:bg-primary-hover shadow-sm",
    secondary: "bg-white text-ink border border-line-strong hover:border-ink-soft",
    ghost: "text-ink-soft hover:text-ink",
    light: "bg-white text-ink hover:bg-primary-soft",
    "ghost-light": "text-white/80 hover:text-white border border-white/30 hover:border-white/60",
  };
  const cls = `${base} ${sizes} ${variants[variant]}`;
  const inner = (
    <>
      {children}
      {withArrow && <ArrowRight size={16} strokeWidth={2} />}
    </>
  );
  if (/^https?:/.test(to)) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  );
}

export function Badge({ children, tone = "primary" }: { children: ReactNode; tone?: "primary" | "accent" | "success" }) {
  const tones: Record<string, string> = {
    primary: "bg-primary-soft text-primary-hover",
    accent: "bg-accent-soft text-accent",
    success: "bg-success-soft text-success",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold tracking-wide ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function Eyebrow({ children, dark = false }: { children: ReactNode; dark?: boolean }) {
  return (
    <div className={`font-display text-[12px] font-semibold uppercase tracking-[0.14em] ${dark ? "text-white/70" : "text-ink-faint"}`}>{children}</div>
  );
}


// Temporary stock photo for every "photo here" slot until the real shots land.
export const PLACEHOLDER_PHOTO = "https://framerusercontent.com/images/gvrZUoowU6t5rwpwtONkkVWoFSU.png";

export function ImgPlaceholder({
  label,
  ratio = "aspect-[16/9]",
  className = "",
  src,
}: {
  label: string;
  ratio?: string;
  className?: string;
  /** CMS image URL (falls back to the shared placeholder until one is uploaded). */
  src?: string;
}) {
  return (
    <div className={`${ratio} w-full overflow-hidden rounded-2xl border border-line bg-bg-subtle ${className}`}>
      <img src={src || PLACEHOLDER_PHOTO} alt={label} loading="lazy" decoding="async" className="h-full w-full object-cover" />
    </div>
  );
}

export function VideoPlaceholder({ label }: { label: string }) {
  return (
    <div className="aspect-[16/9] w-full overflow-hidden rounded-2xl border border-line bg-bg-subtle">
      <BoardMock />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export const POST_AUTHOR = "Done-it";

export function Avatar({ name = POST_AUTHOR, size = 28 }: { name?: string; size?: number }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-primary-soft font-display font-semibold text-primary-hover"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-line bg-white px-3 py-1.5 text-[12px] font-medium text-ink-soft">
      {children}
    </span>
  );
}

/** Two-cell card footer — "Written by / name" and "Posted on / date", split apart. */
function PostFooter({ author, date, avatar = false }: { author: string; date?: string; avatar?: boolean }) {
  const { t } = useLocale();
  return (
    <div className="mt-5 flex items-center justify-between border-t border-line pt-4">
      <div className="flex items-center gap-2.5">
        {avatar && <Avatar name={author} size={36} />}
        <div className="flex flex-col">
          <span className="text-[12px] leading-tight text-ink-faint">{t("common:post.writtenBy")}</span>
          <span className="text-[14px] font-medium leading-tight text-ink">{author}</span>
        </div>
      </div>
      {date && (
        <div className="flex flex-col">
          <span className="text-[12px] leading-tight text-ink-faint">{t("common:post.postedOn")}</span>
          <span className="text-[14px] font-medium leading-tight text-ink">{date}</span>
        </div>
      )}
    </div>
  );
}

/**
 * PostCard — faithful rebuild of the OneDollar blog card. Placeholder cover (zooms
 * on hover), a row of grey tag pills + bullet + read-time, headline, divider, and a
 * two-cell "Written by / Posted on" footer. `featured` switches to the larger white
 * shadowed container with an excerpt and an author avatar. Reused on the blog index
 * (Resources) and the article "Related" grid (BlogPost).
 */
export function PostCard({
  to,
  tag,
  title,
  read,
  lead,
  date,
  author = POST_AUTHOR,
  featured = false,
  ratio,
  image,
}: {
  to: string;
  tag: string;
  title: string;
  read?: string;
  lead?: string;
  date?: string;
  author?: string;
  featured?: boolean;
  ratio?: string;
  /** Cover photo for the article; falls back to the generic placeholder. */
  image?: string;
}) {
  const { t } = useLocale();
  const aspect = ratio ?? (featured ? "aspect-[2/1] md:aspect-[12/5]" : "aspect-[16/10]");
  const cover = (
    <div className={`relative ${aspect} w-full overflow-hidden ${featured ? "rounded-2xl" : "rounded-xl"} border border-line bg-bg-subtle`}>
      <img
        src={image ?? PLACEHOLDER_PHOTO}
        alt={tag}
        loading="lazy" decoding="async"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
      />
    </div>
  );
  const meta = (
    <div className="flex items-center gap-2.5">
      {featured && <Pill>{t("common:post.featured")}</Pill>}
      <Pill>{tag}</Pill>
      {read && (
        <>
          <span className="h-1 w-1 shrink-0 rounded-full bg-line-strong" />
          <span className="text-[12px] text-ink-faint">{t("common:post.readSuffix", { time: read })}</span>
        </>
      )}
    </div>
  );
  const body = (
    <div className={featured ? "px-4 pb-2 pt-7" : "px-3 pb-1 pt-4"}>
      {meta}
      <h3
        className={
          featured
            ? "mt-4 line-clamp-2 font-display text-[26px] font-semibold leading-[1.15] tracking-tight text-ink transition-colors group-hover:text-primary md:text-[32px]"
            : "mt-4 line-clamp-2 font-display text-[20px] font-semibold leading-snug tracking-tight text-ink transition-colors group-hover:text-primary"
        }
      >
        {title}
      </h3>
      {featured && lead && <p className="mt-3 line-clamp-2 text-[16px] leading-relaxed text-ink-soft">{lead}</p>}
      <PostFooter author={author} date={date} avatar={featured} />
    </div>
  );

  if (featured) {
    return (
      <Link
        to={to}
        className="group block rounded-3xl border-8 border-white bg-bg-subtle p-3 shadow-[0_1px_3px_rgba(15,23,42,0.05)] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_16px_36px_-14px_rgba(15,23,42,0.18)]"
      >
        {cover}
        {body}
      </Link>
    );
  }
  return (
    <Link
      to={to}
      className="group block rounded-2xl border-8 border-white bg-bg-subtle p-3 shadow-[0_1px_3px_rgba(15,23,42,0.05)] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_16px_36px_-14px_rgba(15,23,42,0.18)]"
    >
      {cover}
      {body}
    </Link>
  );
}

